import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "./Admin";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, Mail, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, formatDistanceToNowStrict } from "date-fns";
import { toast } from "sonner";

type Event = {
  ts: string;
  kind: "attempt" | "lock" | "booking" | "stripe" | "email";
  label: string;
  detail?: string;
  badge?: string;
  raw?: any;
  bookingId?: string;
};

const kindCls: Record<string, string> = {
  attempt: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  lock: "border-sky-500/40 bg-sky-500/10 text-sky-200",
  booking: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  stripe: "border-violet-500/40 bg-violet-500/10 text-violet-200",
  email: "border-primary/40 bg-primary/10 text-primary",
};

const AdminSlotTimeline = () => {
  const [params, setParams] = useSearchParams();
  const [studios, setStudios] = useState<{ id: string; name: string }[]>([]);
  const [studioId, setStudioId] = useState(params.get("studio_id") ?? "");
  const [startStr, setStartStr] = useState(params.get("start") ?? "");
  const [endStr, setEndStr] = useState(params.get("end") ?? "");
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [userFilter, setUserFilter] = useState(params.get("user_id") ?? "");
  const [sessionFilter, setSessionFilter] = useState(params.get("session") ?? "");
  const [stripeFilter, setStripeFilter] = useState(params.get("stripe") ?? "");
  const [kindFilter, setKindFilter] = useState<string>(params.get("kind") ?? "all");
  const [activeLocks, setActiveLocks] = useState<any[]>([]);
  const [now, setNow] = useState(Date.now());
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    supabase.from("studios").select("id,name").order("display_order").then(({ data }) => setStudios((data ?? []) as any));
  }, []);

  const load = async () => {
    if (!studioId || !startStr || !endStr) return;
    setLoading(true);
    setEvents([]);
    setActiveLocks([]);
    const start = new Date(startStr).toISOString();
    const end = new Date(endStr).toISOString();
    const all: Event[] = [];

    // Slot attempts (same studio + overlapping time)
    const { data: attempts } = await supabase
      .from("slot_attempt_log" as any)
      .select("*")
      .eq("studio_id", studioId)
      .lte("start_at", end)
      .gte("end_at", start)
      .order("created_at", { ascending: true })
      .limit(200);
    (attempts ?? []).forEach((a: any) => {
      all.push({
        ts: a.created_at,
        kind: a.outcome === "acquired" ? "lock" : "attempt",
        label: a.outcome,
        detail: `${a.reason ?? ""}${a.session_token ? ` · token ${a.session_token.slice(0, 12)}…` : ""}`,
        badge: a.outcome,
        raw: a,
      });
    });

    // Bookings touching this slot
    const { data: bookings } = await supabase
      .from("bookings")
      .select("id,start_at,end_at,status,payment_status,total,amount_paid,created_at,updated_at,stripe_checkout_session_id,confirmation_email_sent_at,notes")
      .eq("studio_id", studioId)
      .lte("start_at", end)
      .gte("end_at", start)
      .order("created_at", { ascending: true });
    const bookingIds: string[] = [];
    const sessionIds: string[] = [];
    (bookings ?? []).forEach((b: any) => {
      bookingIds.push(b.id);
      if (b.stripe_checkout_session_id) sessionIds.push(b.stripe_checkout_session_id);
      all.push({
        ts: b.created_at,
        kind: "booking",
        label: `Booking created (${b.status})`,
        detail: `id ${b.id.slice(0, 8)} · ${b.payment_status} · total $${Number(b.total ?? 0).toFixed(2)}`,
        raw: b,
        bookingId: b.id,
      });
      if (b.updated_at && b.updated_at !== b.created_at) {
        all.push({
          ts: b.updated_at,
          kind: "booking",
          label: `Booking updated → ${b.status}/${b.payment_status}`,
          detail: `paid $${Number(b.amount_paid ?? 0).toFixed(2)}`,
          raw: b,
          bookingId: b.id,
        });
      }
      if (b.confirmation_email_sent_at) {
        all.push({
          ts: b.confirmation_email_sent_at,
          kind: "email",
          label: "Confirmation email queued",
          detail: `booking ${b.id.slice(0, 8)}`,
          bookingId: b.id,
        });
      }
    });

    // Stripe webhook events for these checkout sessions / booking ids
    if (bookingIds.length || sessionIds.length) {
      const { data: events } = await supabase
        .from("stripe_webhook_events")
        .select("id,type,status,received_at,error_message,payload")
        .order("received_at", { ascending: true })
        .limit(300);
      (events ?? []).forEach((e: any) => {
        const p = e.payload ?? {};
        const obj = p?.data?.object ?? {};
        const sessionId = obj.id || obj.payment_intent || "";
        const meta = obj.metadata ?? {};
        const matchesBooking = meta.booking_id && bookingIds.includes(meta.booking_id);
        const matchesSession = sessionIds.includes(sessionId);
        if (!matchesBooking && !matchesSession) return;
        all.push({
          ts: e.received_at,
          kind: "stripe",
          label: e.type,
          detail: `${e.status}${e.error_message ? " · " + e.error_message : ""}${sessionId ? " · " + String(sessionId).slice(0, 18) : ""}`,
          raw: e,
        });
      });
    }

    // Emails sent referencing these bookings
    if (bookingIds.length) {
      const { data: emails } = await supabase
        .from("email_send_log")
        .select("id,message_id,template_name,recipient_email,status,error_message,metadata,created_at")
        .order("created_at", { ascending: true })
        .limit(300);
      (emails ?? []).forEach((m: any) => {
        const md = m.metadata ?? {};
        const bid = md.booking_id || md.bookingId;
        if (!bid || !bookingIds.includes(bid)) return;
        all.push({
          ts: m.created_at,
          kind: "email",
          label: `${m.template_name} → ${m.status}`,
          detail: `${m.recipient_email}${m.error_message ? " · " + m.error_message : ""}`,
          raw: m,
          bookingId: bid,
        });
      });
    }

    all.sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
    setEvents(all);

    // Active (non-expired) slot locks overlapping the window
    const { data: locks } = await supabase
      .from("slot_locks")
      .select("id,studio_id,start_at,end_at,session_token,user_id,expires_at,created_at")
      .eq("studio_id", studioId)
      .lte("start_at", end)
      .gte("end_at", start)
      .order("expires_at", { ascending: true });
    setActiveLocks((locks ?? []) as any);

    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const apply = () => {
    const p = new URLSearchParams();
    if (studioId) p.set("studio_id", studioId);
    if (startStr) p.set("start", startStr);
    if (endStr) p.set("end", endStr);
    if (search) p.set("q", search);
    if (userFilter) p.set("user_id", userFilter);
    if (sessionFilter) p.set("session", sessionFilter);
    if (stripeFilter) p.set("stripe", stripeFilter);
    if (kindFilter && kindFilter !== "all") p.set("kind", kindFilter);
    setParams(p);
    load();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (kindFilter !== "all" && e.kind !== kindFilter) return false;
      if (userFilter && (e.raw?.user_id ?? "") !== userFilter) return false;
      if (sessionFilter) {
        const tok = e.raw?.session_token ?? "";
        if (!tok || !tok.includes(sessionFilter)) return false;
      }
      if (stripeFilter) {
        const sid = e.raw?.stripe_checkout_session_id
          ?? e.raw?.payload?.data?.object?.id
          ?? e.raw?.payload?.data?.object?.payment_intent
          ?? "";
        if (!String(sid).includes(stripeFilter)) return false;
      }
      if (q) {
        const hay = `${e.label} ${e.detail ?? ""} ${JSON.stringify(e.raw ?? {})}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [events, search, userFilter, sessionFilter, stripeFilter, kindFilter]);

  const grouped = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach((e) => { counts[e.kind] = (counts[e.kind] ?? 0) + 1; });
    return counts;
  }, [filtered]);

  const resendConfirmation = async (bookingId: string) => {
    setResendingId(bookingId);
    const { data, error } = await supabase.functions.invoke("resend-booking-confirmation", {
      body: { booking_id: bookingId },
    });
    setResendingId(null);
    if (error || (data as any)?.error) {
      toast.error(error?.message ?? (data as any)?.error ?? "Failed");
      return;
    }
    toast.success("Confirmation email resent");
    load();
  };

  const cleanupLocks = async () => {
    setCleaning(true);
    const { data, error } = await supabase.functions.invoke("cleanup-expired-locks", { body: {} });
    setCleaning(false);
    if (error) { toast.error(error.message); return; }
    toast.success(`Removed ${(data as any)?.deleted_locks ?? 0} expired locks`);
    load();
  };

  return (
    <AdminLayout title="SLOT TIMELINE">
      <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-4">
        Unified timeline for a slot: lock attempts, conflict checks, bookings, Stripe events, and emails.
      </p>

      <div className="bg-panel border border-border p-4 grid md:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">Studio</Label>
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger><SelectValue placeholder="Pick studio" /></SelectTrigger>
            <SelectContent>
              {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">Start (ISO)</Label>
          <Input value={startStr} onChange={(e) => setStartStr(e.target.value)} placeholder="2026-06-22T14:00:00Z" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">End (ISO)</Label>
          <Input value={endStr} onChange={(e) => setEndStr(e.target.value)} placeholder="2026-06-22T18:00:00Z" />
        </div>
        <div className="flex items-end">
          <Button onClick={apply} className="w-full"><RefreshCw className="w-3 h-3 mr-2" />Load timeline</Button>
        </div>
      </div>

      <div className="bg-panel border border-border p-4 grid md:grid-cols-5 gap-3 mb-4">
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">Search</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="text, status, reason…" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">User id</Label>
          <Input value={userFilter} onChange={(e) => setUserFilter(e.target.value)} placeholder="uuid" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">Session token</Label>
          <Input value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)} placeholder="contains…" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">Stripe session id</Label>
          <Input value={stripeFilter} onChange={(e) => setStripeFilter(e.target.value)} placeholder="cs_..." />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest">Kind</Label>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="attempt">Attempt</SelectItem>
              <SelectItem value="lock">Lock</SelectItem>
              <SelectItem value="booking">Booking</SelectItem>
              <SelectItem value="stripe">Stripe</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-panel border border-border p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Active slot locks ({activeLocks.length})
          </div>
          <Button size="sm" variant="outline" onClick={cleanupLocks} disabled={cleaning}>
            {cleaning ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Trash2 className="w-3 h-3 mr-2" />}
            Run cleanup now
          </Button>
        </div>
        {activeLocks.length === 0 ? (
          <div className="text-xs text-muted-foreground">No active locks in this window.</div>
        ) : (
          <div className="space-y-1">
            {activeLocks.map((l) => {
              const exp = new Date(l.expires_at).getTime();
              const remaining = Math.max(0, Math.floor((exp - now) / 1000));
              const expired = exp <= now;
              const age = formatDistanceToNowStrict(new Date(l.created_at));
              return (
                <div key={l.id} className="flex flex-wrap items-center gap-3 text-[11px] font-mono">
                  <span className={`px-2 py-1 border ${expired ? "border-destructive/50 text-destructive" : "border-sky-500/40 text-sky-200"}`}>
                    {expired ? "EXPIRED" : `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`}
                  </span>
                  <span className="text-muted-foreground">age {age}</span>
                  <span>token {String(l.session_token).slice(0, 16)}…</span>
                  <span className="text-muted-foreground">user {l.user_id ? String(l.user_id).slice(0, 8) : "anon"}</span>
                  <span className="text-muted-foreground">{format(new Date(l.start_at), "MMM d HH:mm")} → {format(new Date(l.end_at), "HH:mm")}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-4 text-[10px] font-mono uppercase tracking-widest">
        {Object.entries(grouped).map(([k, v]) => (
          <span key={k} className={`px-2 py-1 border ${kindCls[k] ?? "border-border"}`}>{k}: {v}</span>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-panel border border-border p-8 text-center text-sm text-muted-foreground font-body">
          {studioId && startStr ? "No events match the current filters." : "Pick a studio + time range to view the timeline."}
        </div>
      ) : (
        <div className="bg-panel border border-border divide-y divide-border">
          {filtered.map((e, i) => (
            <div key={i} className="p-4 flex items-start gap-4">
              <div className="text-[10px] font-mono text-muted-foreground whitespace-nowrap w-40">
                {format(new Date(e.ts), "MMM d HH:mm:ss")}
              </div>
              <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${kindCls[e.kind]}`}>{e.kind}</span>
              <div className="flex-1 text-xs font-body">
                <div className="font-mono">{e.label}</div>
                {e.detail && <div className="text-muted-foreground mt-1">{e.detail}</div>}
              </div>
              {e.bookingId && (e.kind === "booking" || e.kind === "email") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resendConfirmation(e.bookingId!)}
                  disabled={resendingId === e.bookingId}
                >
                  {resendingId === e.bookingId
                    ? <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    : <Mail className="w-3 h-3 mr-2" />}
                  Resend
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSlotTimeline;